import { Button, Menu, Popover, Tabs, Tooltip } from 'antd';
import React, { useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import ImageQuality from './ImageQuality';
import './index.scss';
import ConnectionStatus from './ConnectionStatus';
import {
  DownloadOutlined,
  SettingOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import { openMessage } from '../../../redux/actions';
import { DECODED_ACCESS_TOKEN, NOTIFIES } from '../../../constants/system.keys';
import storageService from '../../../services/storage.service';
import moment from 'moment';
import { getExportReportCameraIncidentApi } from '../../../api/reportApi';
import { useSelector } from 'react-redux';

const { TabPane } = Tabs;

const MonitoringSystem = () => {
  const { t } = useTranslation();
  const [activeKey, setActiveKey] = useState('1');
  const [open, setOpen] = useState(false);
  const [intervalMs, setIntervalMs] = useState(0);
  const reloadRefs = useRef({});
  const [loadingReport, setLoadingReport] = useState(false);
  const { namespace } = useSelector((state) => state);
  const [exportDate, setExportDate] = useState(moment());

  const handleOpenChange = (newOpen) => {
    setOpen(newOpen);
  };

  const handleIntervalChange = (e) => {
    const selectedValue = parseInt(e.key, 10);
    setIntervalMs(selectedValue);
    setOpen(false);
  };

  const handleManualReload = useCallback(() => {
    if (reloadRefs.current[activeKey]) {
      reloadRefs.current[activeKey]();
      openMessage(
        NOTIFIES.SUCCESS,
        t('The data has been updated to the latest version.'),
      );
    }
  }, [activeKey, t]);

  const registerReloadFunction = useCallback((key, reloadFn) => {
    reloadRefs.current[key] = reloadFn;
  }, []);

  const onExport = async () => {
    try {
      setLoadingReport(true);
      const token =
        storageService.getInternalStorageByKey(DECODED_ACCESS_TOKEN);

      const dataBody = {
        command: 'report_camera_incident',
        params: JSON.stringify({
          customerNamespace: namespace,
          isDownload: 1,
          fromTime: moment(exportDate).startOf('day').valueOf(),
          toTime: moment(exportDate).endOf('day').valueOf(),
        }),
      };

      const dataRes = await getExportReportCameraIncidentApi(
        token,
        { namespace },
        dataBody,
      );

      if (!dataRes?.data) {
        openMessage(NOTIFIES.WARNING, 'No data export');
        setLoadingReport(false);
        return;
      }

      const blob = new Blob([dataRes.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });

      const url = window.URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = url;
      a.download = `report_camera_incident_${moment(exportDate).format('YYYY-MM-DD')}.xlsx`;
      a.click();

      window.URL.revokeObjectURL(url);
    } catch (error) {
      openMessage(
        NOTIFIES.WARNING,
        error?.response?.data?.message ?? 'Something went wrong',
      );
    } finally {
      setLoadingReport(false);
    }
  };

  return (
    <Tabs
      defaultActiveKey="1"
      activeKey={activeKey}
      className="monitoring-dashboard-tabs"
      onChange={(activeKey) => setActiveKey(activeKey)}
      tabBarExtraContent={
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            cursor: 'pointer',
          }}
        >
          <Button
            icon={<DownloadOutlined />}
            type="primary"
            loading={loadingReport}
            disabled={loadingReport}
            onClick={onExport}
          >
            {t('Export')}
          </Button>
          <Tooltip title={t('Reload manual')}>
            <Button
              type="primary"
              icon={<SyncOutlined />}
              onClick={handleManualReload}
            />
          </Tooltip>
          <Popover
            overlayClassName="monitoring-dashboard-popover-setting"
            content={
              <Menu
                onClick={handleIntervalChange}
                selectedKeys={[String(intervalMs)]}
              >
                <Menu.Item key="0">{t('Manual')} </Menu.Item>
                <Menu.Item key="15000">{t('Interval in 15 seconds')}</Menu.Item>
                <Menu.Item key="30000">{t('Interval in 30 seconds')}</Menu.Item>
                <Menu.Item key="45000">{t('Interval in 45 seconds')}</Menu.Item>
                <Menu.Item key="60000">{t('Interval in 1 minutes')} </Menu.Item>
              </Menu>
            }
            trigger="click"
            open={open}
            onOpenChange={handleOpenChange}
          >
            <SettingOutlined width={24} height={24} />
          </Popover>
        </div>
      }
    >
      <TabPane tab={t('Image Quality')} key="1">
        <ImageQuality
          isActive={activeKey === '1'}
          intervalMs={intervalMs}
          onRegisterReload={(fn) => registerReloadFunction('1', fn)}
          setExportDate={setExportDate}
        />
      </TabPane>
      <TabPane tab={t('Connection Status')} key="2">
        <ConnectionStatus
          isActive={activeKey === '2'}
          intervalMs={intervalMs}
          onRegisterReload={(fn) => registerReloadFunction('2', fn)}
          setExportDate={setExportDate}
        />
      </TabPane>
    </Tabs>
  );
};

export default MonitoringSystem;
